import { Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useSearchParams } from 'react-router-dom'
import { lazyWithReload } from '@/shared/utils/lazy-with-reload'
import DashboardLayout from '@/clientWeb/components/layout/DashboardLayout'
import NotFoundPage from '@/clientWeb/components/errors/NotFoundPage'
import { ErrorBoundary } from '@/clientWeb/components/ui/error-boundary'
import { AUTH_ROUTES } from '@/shared/constants/routes.constants'
import AuthHydrator from './guards/AuthHydrator'
import ProtectedRoute from './guards/ProtectedRoute'
import PublicOnlyRoute from './guards/PublicOnlyRoute'

// Auth pages — lazy-loaded.
const LoginPage = lazyWithReload(() => import('@/features/auth/pages/LoginPage'))
const OidcCallbackPage = lazyWithReload(() => import('@/features/auth/pages/OidcCallbackPage'))
const SignupPage = lazyWithReload(() => import('@/features/auth/pages/SignupPage'))
const ForgotPasswordPage = lazyWithReload(() => import('@/features/auth/pages/ForgotPasswordPage'))
const ResetPasswordPage = lazyWithReload(() => import('@/features/auth/pages/ResetPasswordPage'))
const VerifyEmailPage = lazyWithReload(() => import('@/features/auth/pages/VerifyEmailPage'))
const JoinMeetPage = lazyWithReload(() => import('@/features/auth/pages/JoinMeetPage'))
const GuestJoinPage = lazyWithReload(() => import('@/features/auth/pages/GuestJoinPage'))
const EmbedClosedPage = lazyWithReload(() => import('@/features/embed/EmbedClosedPage'))
const ScreenSharePage = lazyWithReload(() => import('@/features/screen-share-only/pages/ScreenSharePage'))

// Non-auth pages — eager imports until each module's refactor pass.
import {
  MeetingsPage,
  MeetingDetailsPage,
  PastMeetingPage,
  AttendancePage,
  ScheduleMeeting,
  MeetingGuard,
} from '@/features/meetings'
import { PresencePage } from '@/features/messenger'
import {
  WebinarList,
  CreateWebinar,
  WebinarDetails,
  WebinarAnalytics,
  PastWebinarDetails,
  WebinarRegistrationPage,
  WebinarAnalyticsDashboard,
  LiveWebinarsPage,
  TotalWebinarsPage,
  AttendeeList,
  PanelistList,
  RecordingView,
  FeedbackSurvey,
  WebinarPolls,
  QAPage,
} from '@/features/webinar'
import {
  AnalyticsDashboard,
  MeetingsList,
  MeetingDetail,
  TopUsersPage,
  WorldMapPage,
  LiveMeetingsPage,
  RecordingsPage,
  PollsAnalyticsPage,
  StandupMeetingsPage,
} from '@/features/analytics'
import { Calendar } from '@/features/calendar'
import { Marketplace } from '@/clientWeb/components/marketplace'
import {
  SettingsLayout,
  MyProfile,
  LicensesManagement,
  PaymentHistory,
  Languages,
  Transcription,
  Telepresence,
  AddressBook,
  HelpFeedback,
  About,
  GeneralSettings,
  MyFiles,
  PricingPage,
  CartPage,
  SubscriptionResult,
} from '@/features/settings'
import RoomsPage from '@/clientWeb/components/rooms'
import AttendanceReports from '@/clientWeb/components/attendance/AttendanceReports'
import VcApp from '@/clientVc/VcApp'

function RootRedirect() {
  const [params] = useSearchParams()
  const code = params.get('code')
  // The meeting route admits signed-out visitors on its own, so no role flag.
  if (code) return <Navigate to={`/meeting/${code}`} replace />
  return <Navigate to={AUTH_ROUTES.LOGIN} replace />
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <AuthHydrator>
      <Suspense fallback={null}>
        <Routes>
          {/* Root redirect — ?code=<id> opens the meeting as a guest; otherwise go to login */}
          <Route path="/" element={<RootRedirect />} />

          {/* Authentication — public-only (already signed-in users get bounced) */}
          <Route path={AUTH_ROUTES.LOGIN} element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
          <Route path={AUTH_ROUTES.OIDC_CALLBACK} element={<OidcCallbackPage />} />
          <Route path={AUTH_ROUTES.SIGNUP} element={<PublicOnlyRoute><SignupPage /></PublicOnlyRoute>} />
          <Route path={AUTH_ROUTES.FORGOT_PASSWORD} element={<PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>} />
          <Route path={AUTH_ROUTES.RESET_PASSWORD} element={<PublicOnlyRoute><ResetPasswordPage /></PublicOnlyRoute>} />
          <Route path={AUTH_ROUTES.VERIFY_EMAIL} element={<PublicOnlyRoute><VerifyEmailPage /></PublicOnlyRoute>} />
          <Route path={AUTH_ROUTES.JOIN_MEET} element={<JoinMeetPage />} />
          <Route path="/invite/:token" element={<GuestJoinPage />} />

          {/* Webinar public registration (no layout) */}
          <Route path="/webinar/:id/register" element={<WebinarRegistrationPage />} />

          {/* Subscription result pages (full-screen, no layout) */}
          <Route path="/settings/licenses/success" element={<SubscriptionResult success={true} />} />
          <Route path="/settings/licenses/failed" element={<SubscriptionResult success={false} />} />

          {/* VC Client — full-screen meeting (no sidebar/header). MeetingGuard
              verifies existence, schedule window, lock state and password
              before VcApp mounts. */}
          <Route
            path="/meeting/:meetingId"
            element={
              <ProtectedRoute allowGuest>
                <MeetingGuard>
                  <VcApp />
                </MeetingGuard>
              </ProtectedRoute>
            }
          />

          {/* Screen Share — in a meeting as a screen only. Signed-in users
              only (no allowGuest), and not under MeetingGuard/VcApp: no
              password, no camera preview, no meeting UI. */}
          <Route
            path="/share/:meetingId"
            element={
              <ProtectedRoute>
                <ScreenSharePage />
              </ProtectedRoute>
            }
          />

          {/* Where a meeting framed by another application goes instead of the
              dashboard — see features/embed/meeting-exit.ts. */}
          <Route path="/embed/closed" element={<EmbedClosedPage />} />

          {/* App modules with shared layout (sidebar/header via Outlet) */}
          <Route element={<ProtectedRoute><ErrorBoundary><DashboardLayout /></ErrorBoundary></ProtectedRoute>}>
            <Route path="/meetings" element={<MeetingsPage />} />
            <Route path="/meetings/:id" element={<MeetingDetailsPage />} />
            <Route path="/meetings/:id/past" element={<PastMeetingPage />} />
            <Route path="/meetings/:id/attendance" element={<AttendancePage />} />
            <Route path="/schedule" element={<ScheduleMeeting />} />
            <Route path="/chats" element={<PresencePage />} />
            {/* Group chat invites render the chat interface itself — there is
                no separate invite screen. PresencePage reads the id from the
                path, joins if needed, and opens the thread.
                NOT /invite/:token: that belongs to GuestJoinPage for meeting
                guest invites. */}
            <Route path="/chats/invite/:token" element={<PresencePage />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/webinar" element={<WebinarList />} />
            <Route path="/webinar/analytics" element={<WebinarAnalyticsDashboard />} />
            <Route path="/webinar/analytics/live" element={<LiveWebinarsPage />} />
            <Route path="/webinar/analytics/webinars" element={<TotalWebinarsPage />} />
            <Route path="/webinar/create" element={<CreateWebinar />} />
            <Route path="/webinar/:id" element={<WebinarDetails />} />
            <Route path="/webinar/:id/past" element={<PastWebinarDetails />} />
            <Route path="/webinar/:id/analytics" element={<WebinarAnalytics />} />
            <Route path="/webinar/:id/analytics/attendees" element={<AttendeeList />} />
            <Route path="/webinar/:id/analytics/panelists" element={<PanelistList />} />
            <Route path="/webinar/:id/analytics/recordings" element={<RecordingView />} />
            <Route path="/webinar/:id/analytics/feedback" element={<FeedbackSurvey />} />
            <Route path="/webinar/:id/analytics/polls" element={<WebinarPolls />} />
            <Route path="/webinar/:id/analytics/qa" element={<QAPage />} />
            <Route path="/analytics" element={<AnalyticsDashboard />} />
            <Route path="/analytics/meetings" element={<MeetingsList />} />
            <Route path="/analytics/meetings/:sessionId/:meetingId" element={<MeetingDetail />} />
            <Route path="/analytics/recordings" element={<RecordingsPage />} />
            <Route path="/analytics/polls" element={<PollsAnalyticsPage />} />
            <Route path="/analytics/standup" element={<StandupMeetingsPage />} />
            <Route path="/analytics/top-users" element={<TopUsersPage />} />
            <Route path="/analytics/world-map" element={<WorldMapPage />} />
            <Route path="/analytics/live-meetings" element={<LiveMeetingsPage />} />
            <Route path="/meetings/:id/attendance-reports" element={<AttendanceReports />} />
            <Route path="/market" element={<Marketplace />} />
            <Route path="/rooms" element={<RoomsPage />} />
            <Route path="/settings" element={<SettingsLayout />}>
              <Route index element={<MyProfile />} />
              <Route path="general" element={<GeneralSettings />} />
              <Route path="licenses" element={<LicensesManagement />} />
              <Route path="payment-history" element={<PaymentHistory />} />
              <Route path="languages" element={<Languages />} />
              <Route path="transcription" element={<Transcription />} />
              <Route path="telepresence" element={<Telepresence />} />
              <Route path="address-book" element={<AddressBook />} />
              <Route path="help" element={<HelpFeedback />} />
              <Route path="about" element={<About />} />
              <Route path="my-files" element={<MyFiles />} />
            </Route>
            <Route path="/settings/licenses/pricing" element={<PricingPage />} />
            <Route path="/settings/licenses/cart" element={<CartPage />} />
          </Route>

          {/* Backward-compatible redirects from old paths */}
          <Route path="/Meetings" element={<Navigate to="/meetings" replace />} />
          <Route path="/Meetings/presence" element={<Navigate to="/chats" replace />} />
          <Route path="/Meetings/calendar" element={<Navigate to="/calendar" replace />} />
          <Route path="/Meetings/webinar" element={<Navigate to="/webinar" replace />} />
          <Route path="/Meetings/analytics" element={<Navigate to="/analytics" replace />} />
          <Route path="/Meetings/market" element={<Navigate to="/market" replace />} />
          <Route path="/Meetings/settings" element={<Navigate to="/settings" replace />} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      </AuthHydrator>
    </BrowserRouter>
  )
}
